import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { usePaystackPayment } from "react-paystack";
import { PaystackProps } from "react-paystack/dist/types";
import { useNavigate } from "react-router-dom";
import Icon from "src/components/Icon";
import InnerLoading from "src/components/InnerLoading";
import { AppContext, ValueProps } from "src/context";
import {
  GenerateInviteFeePaymentMetadata,
  SendInvitations,
  VerifyTransaction,
} from "src/graphql/mutations";
import { FilterInvitationList, GetUsername } from "src/graphql/queries";
import useAppStore from "src/store/utils";
import {
  Channel,
  getUsername,
  invitationListFilter,
  sendInvitations,
  sendInvitationsVariables,
  verifyTransaction,
  verifyTransactionVariables,
} from "src/types/api.d";
import {
  generateInviteFeePaymentMetadata,
  generateInviteFeePaymentMetadataVariables,
} from "src/utils/gql_types";
import { SendInvitesStepsTypes } from "..";
import CostDisplay from "./CostDisplay";
import InviteOptions from "./InviteOptions";
import SendInvitesModalFooter from "./SendInvitesModalFooter";

interface PaymentOptionsProps {
  setStep: React.Dispatch<React.SetStateAction<SendInvitesStepsTypes>>;
  cost: number | undefined;
  setOpenInviteModal: React.Dispatch<React.SetStateAction<boolean>>;
  channel: string;
  invitationIDs: string[];
  invitationFilter: invitationListFilter;
}

const PaymentOptions = ({
  setStep,
  cost,
  setOpenInviteModal,
  invitationIDs,
  channel,
  invitationFilter,
}: PaymentOptionsProps) => {
  const [active, setActive] = useState<string>("");
  const [userDetails, setUserDetails] = useState<getUsername>();
  const contextValue = useContext<ValueProps | null>(AppContext);
  const isAdmin = contextValue?.isAdmin;
  const { event } = useAppStore((state) => state);

  const navigate = useNavigate();

  useQuery<getUsername>(GetUsername, {
    fetchPolicy: "network-only",
    onCompleted(response) {
      setUserDetails(response);
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const updateActiveElement = (id: string) => {
    setActive(active !== id ? id : "");
  };

  const [sendInvitation, { loading }] = useMutation<
    sendInvitations,
    sendInvitationsVariables
  >(SendInvitations, {
    onCompleted() {
      setStep("inviteSent");
      toast.success("Invites sent successfully");
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const doSendInvitation = () => {
    sendInvitation({
      variables: {
        eventId: event?.uuid,
        req: {
          all: false,
          pending: false,
          channel: channel as Channel,
          list_id: invitationIDs,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: FilterInvitationList,
          fetchPolicy: "network-only",
          variables: {
            eventId: event?.id,
            page: 1,
            pageSize: 1000,
            filter: invitationFilter,
          },
        },
      ],
    });
  };

  const [verifyTransaction] = useMutation<
    verifyTransaction,
    verifyTransactionVariables
  >(VerifyTransaction, {
    onCompleted: (res) => {
      if (res?.verifyTransaction?.reference) {
        doSendInvitation();
      }
    },
    onError(error) {
      // console.log(error);
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  // Mutation for generating payment metadata
  const [generateInviteFeeMetadata] = useMutation<
    generateInviteFeePaymentMetadata,
    generateInviteFeePaymentMetadataVariables
  >(GenerateInviteFeePaymentMetadata, {
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  // Paystack configuration (initially empty, updated in handleSubmit)
  const [paystackConfig, setPaystackConfig] = useState<PaystackProps | null>(
    null
  );

  const handleContinue = async () => {
    if (active === "paystack") {
      try {
        // Call mutation to generate payment metadata
        const { data: mutationData } = await generateInviteFeeMetadata({
          variables: {
            event_id: event?.uuid,
            amount: cost ?? 0,
            currency: "ngn",
            channel: channel as Channel,
            count: invitationIDs?.length,
          },
        });

        const paymentMeta = mutationData?.generateInviteFeePaymentMetadata;

        setPaystackConfig({
          reference: paymentMeta?.payment_reference || "",
          email: userDetails?.user?.email ?? "",
          amount: Math.round((paymentMeta?.amount ?? 0) * 100) || 0,
          publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "",
          metadata: paymentMeta,
        });
      } catch (error) {
        toast.error("Failed to initiate payment");
        console.error(error);
      }
      setOpenInviteModal(false);
    } else {
      doSendInvitation();
    }
  };

  const onSuccessWithPaystack = () => {
    verifyTransaction({
      variables: {
        reference: paystackConfig?.reference || "",
      },
    });
  };

  // Always call the hook unconditionally with a default config
  const initializePayment = usePaystackPayment(
    paystackConfig || { reference: "", email: "", amount: 0, publicKey: "" }
  );

  useEffect(() => {
    if (paystackConfig) {
      setOpenInviteModal(false);
      initializePayment(onSuccessWithPaystack);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paystackConfig]);

  // fetch invite credits details
  const [inviteCredits, setInviteCredits] = useState(0);

  const { loading: inviteCreditsLoading } = useQuery(
    gql`
      query event($eventId: Int!) {
        event(event_id: $eventId) {
          invite_credit {
            whatsapp
          }
        }
      }
    `,
    {
      variables: { eventId: event?.id },
      onCompleted(data) {
        setInviteCredits(data?.event?.invite_credit?.whatsapp);
      },
    }
  );

  const inviteCreditsTag = (text: string) => (
    <div className="flex items-center gap-2">
      <span className="text-sm">{text}</span>
      <Icon iconName="coin" />
    </div>
  );

  return (
    <>
      <div className="px-5 pb-1 pt-10 lg:px-10">
        <h1 className="mb-10 text-2xl font-medium">
          How would you want to pay?
        </h1>
        <CostDisplay cost={cost} />
        {inviteCreditsLoading ? (
          <div className="flex w-full justify-center py-8">
            <InnerLoading />
          </div>
        ) : (
          <div className="mb-4 flex flex-col gap-4 pt-6">
            <InviteOptions
              type="Paystack"
              active={active === "paystack"}
              onClick={() => updateActiveElement("paystack")}
            />
            {cost && (
              <InviteOptions
                active={active === "Invite credits"}
                onClick={() => {
                  inviteCredits >= invitationIDs?.length &&
                    updateActiveElement("Invite credits");
                }}
                type="Invite credits"
                description={inviteCreditsTag(inviteCredits.toLocaleString())}
                disabled={inviteCredits < invitationIDs?.length}
              />
            )}
          </div>
        )}
      </div>

      <SendInvitesModalFooter
        firstButtonText={"Go Back"}
        firstButtonFunction={() => setStep("sendInvitesPrompt")}
        secondButtonText={"Continue"}
        secondButtonFunction={handleContinue}
        disableSecondButton={active === "" || inviteCreditsLoading}
        secondButtonLoader={active === "Invite credits" && loading}
      />
    </>
  );
};

export default PaymentOptions;
